/**
 * FilterUIFactory
 *
 * Factory pattern for creating appropriate filter UI strategy
 * based on viewport size and device orientation.
 *
 * SOLID Compliance:
 * - SRP: Only creates strategies
 * - OCP: Extensible (add new strategies without modification)
 * - LSP: All strategies are interchangeable
 * - ISP: Simple factory interface
 * - DIP: Returns abstractions (FilterUIStrategy instances)
 *
 * Usage:
 * const strategy = FilterUIFactory.createStrategy('mobile', 'portrait');
 * const ui = strategy.render(filters, urlStateManager, filterRepository);
 *
 * Location: apps/web/src/components/search/strategies/FilterUIFactory.js
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System
 */

// Strategy imports
import DesktopDrawerStrategy from './DesktopDrawerStrategy'; // ✅ Created in Phase 2
import MobileScrollStrategy from './MobileScrollStrategy'; // ✅ Created in Phase 1
import MobileGridStrategy from './MobileGridStrategy'; // ✅ Created in Phase 3

// Viewport breakpoints (pixels)
const BREAKPOINTS = {
  MOBILE: 768,   // Below this = mobile
  TABLET: 1024,  // Below this = tablet
  DESKTOP: 1280  // Above this = desktop
};

class FilterUIFactory {
  /**
   * Create appropriate strategy based on viewport and orientation
   * @param {string} viewport - 'mobile' | 'tablet' | 'desktop'
   * @param {string} orientation - 'portrait' | 'landscape'
   * @param {Object} abVariant - TODO (A/B Framework): A/B test variant object
   * @returns {FilterUIStrategy} - Appropriate strategy instance
   */
  static createStrategy(viewport, orientation, _abVariant = null) {
    // TODO (A/B Framework): Override strategy based on experiment variant
    // if (abVariant?.experiment === 'progressiveDisclosure') {
    //   switch (abVariant.variant) {
    //     case 'twoFields':
    //       return new MobileScrollStrategy({ fieldsVisible: 2 });
    //     case 'threeFields':
    //       return new MobileScrollStrategy({ fieldsVisible: 3 });
    //     case 'control':
    //       return new MobileScrollStrategy({ fieldsVisible: 4 });
    //   }
    // }

    // Desktop: Always use drawer strategy
    if (viewport === 'desktop') {
      return new DesktopDrawerStrategy();
    }

    // Tablet: Use desktop strategy for now (can be customized later)
    if (viewport === 'tablet') {
      return new DesktopDrawerStrategy();
    }

    // Mobile portrait: Progressive disclosure (2 fields + expand)
    if (viewport === 'mobile' && orientation === 'portrait') {
      return new MobileScrollStrategy();
    }

    // Mobile landscape: 2x2 grid
    if (viewport === 'mobile' && orientation === 'landscape') {
      return new MobileGridStrategy();
    }

    // Fallback: Use MobileScrollStrategy (should rarely be reached)
    console.warn(`[FilterUIFactory] No specific strategy for viewport="${viewport}", orientation="${orientation}". Using MobileScrollStrategy as fallback.`);
    return new MobileScrollStrategy();
  }

  /**
   * Detect viewport from window width
   * @param {number} width - Window width in pixels
   * @returns {string} - 'mobile' | 'tablet' | 'desktop'
   */
  static detectViewport(width) {
    if (width < BREAKPOINTS.MOBILE) {
      return 'mobile';
    }
    if (width < BREAKPOINTS.DESKTOP) {
      return 'tablet';
    }
    return 'desktop';
  }

  /**
   * Detect orientation from window dimensions
   * @param {number} width - Window width in pixels
   * @param {number} height - Window height in pixels
   * @returns {string} - 'portrait' | 'landscape'
   */
  static detectOrientation(width, height) {
    return width > height ? 'landscape' : 'portrait';
  }

  /**
   * Get current viewport and orientation
   * @returns {Object} - { viewport, orientation, width, height }
   */
  static getCurrentEnvironment() {
    const width = window.innerWidth;
    const height = window.innerHeight;

    return {
      viewport: FilterUIFactory.detectViewport(width),
      orientation: FilterUIFactory.detectOrientation(width, height),
      width,
      height
    };
  }

  /**
   * Get breakpoint values (for testing/debugging)
   * @returns {Object} - Breakpoint configuration
   */
  static getBreakpoints() {
    return { ...BREAKPOINTS };
  }
}

export default FilterUIFactory;
